import { Component } from "@theme/component";
import {
  debounce,
  onDocumentLoaded,
  setHeaderMenuStyle,
} from "@theme/utilities";
import { MegaMenuHoverEvent } from "@theme/events";

/** Pointer must rest this long before a closed menu opens. */
const HOVER_OPEN_DELAY_MS = 200;

/** Pointer must stay outside the item and panel this long before the menu closes. */
const HOVER_CLOSE_DELAY_MS = 300;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ["overflowMenu"];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {number}
   */
  #lastOverlayCarouselInteractionAt = 0;

  /** @type {number | null} */
  #hoverOpenTimer = null;

  /** @type {number | null} */
  #hoverCloseTimer = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener("resize", this.#resizeListener);
    document.addEventListener("keydown", this.#onEscape);
    this.overflowMenu?.addEventListener(
      "pointerleave",
      this.#overflowSubmenuListener,
    );
    this.addEventListener(
      "pointerdown",
      this.#trackOverlayCarouselInteraction,
      {
        capture: true,
      },
    );
    this.addEventListener("keydown", this.#trackOverlayCarouselInteraction, {
      capture: true,
    });

    if (this.dataset.devKeepFirstOverlay === "true") {
      queueMicrotask(() => this.#openDevFirstOverlay());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("resize", this.#resizeListener);
    document.removeEventListener("keydown", this.#onEscape);
    this.overflowMenu?.removeEventListener(
      "pointerleave",
      this.#overflowSubmenuListener,
    );
    this.removeEventListener(
      "pointerdown",
      this.#trackOverlayCarouselInteraction,
      { capture: true },
    );
    this.removeEventListener("keydown", this.#trackOverlayCarouselInteraction, {
      capture: true,
    });
    this.#cleanupMutationObserver();
    this.#clearHoverOpenTimer();
    this.#clearHoverCloseTimer();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /** @param {PointerEvent} event */
  #overflowSubmenuListener = (event) => {
    this.deactivate(event);
  };

  /**
   * Close the open submenu and return focus to the control that opened it.
   * @param {KeyboardEvent} event
   */
  #onEscape = (event) => {
    if (event.key !== "Escape" || event.defaultPrevented) return;
    if (this.dataset.devKeepFirstOverlay === "true") return;

    const item = this.#state.activeItem;
    if (!item) return;

    event.preventDefault();
    const trigger = item.closest('[slot="overflow"]')
      ? this.querySelector('[slot="more"] button')
      : (this.#disclosureFor(item) ?? item);
    this.#isRestoringFocus = true;
    if (trigger instanceof HTMLElement) trigger.focus({ preventScroll: true });
    this.#isRestoringFocus = false;
    this.#deactivate(item, true);
  };

  /**
   * Open or close the submenu from its Expand control.
   * @param {Event} event
   */
  toggle = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest(".menu-list__disclosure");
    if (!(button instanceof HTMLElement)) return;

    if (button.getAttribute("aria-expanded") === "true") {
      const item = findMenuItem(button);
      if (item) this.#deactivate(item, true);
      button.focus({ preventScroll: true });
      return;
    }

    this.activate(event);
  };

  /**
   * @param {HTMLElement} item
   * @returns {HTMLElement | null}
   */
  #disclosureFor(item) {
    const disclosure = item.parentElement?.querySelector(
      ".menu-list__disclosure",
    );
    return disclosure instanceof HTMLElement ? disclosure : null;
  }

  /**
   * Keep the link and its Expand control in sync, and lock the panel while closed.
   * @param {HTMLElement | null} item
   * @param {boolean} isExpanded
   */
  #setExpanded(item, isExpanded) {
    if (!(item instanceof HTMLElement)) return;

    const value = isExpanded ? "true" : "false";
    item.dataset.expanded = value;
    const disclosure = this.#disclosureFor(item);
    if (disclosure) disclosure.ariaExpanded = value;

    const submenu = findSubmenu(item);
    if (submenu) submenu.inert = !isExpanded;
  }

  /**
   * @param {Event} event
   */
  #trackOverlayCarouselInteraction = (event) => {
    if (!(event.target instanceof Element)) return;
    if (
      !event.target.closest(
        ".mega-menu__carousel-slideshow, .mega-menu__carousel-controls",
      )
    )
      return;

    this.#lastOverlayCarouselInteractionAt = performance.now();
  };

  /**
   * Dev-only: open the first top-level mega item so overlay layout can be styled without hovering.
   */
  #openDevFirstOverlay() {
    if (!window.matchMedia("(min-width: 1024px)").matches) return;

    const link = this.querySelector(
      'li.menu-list__list-item:not([slot]) [ref="disclosure[]"]',
    );
    const li = link?.closest("li");
    if (!(li instanceof HTMLElement)) return;

    this.activate(/** @type {PointerEvent} */ ({ target: li }));
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /** While Escape returns focus to More, its focus handler must not open the first overflow item. */
  #isRestoringFocus = false;

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return (
      this.refs.overflowMenu?.shadowRoot
        ?.querySelector('[part="overflow-list"]')
        ?.matches(":hover") ?? false
    );
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest("header-component"));
  }

  #clearHoverOpenTimer() {
    if (this.#hoverOpenTimer == null) return;
    clearTimeout(this.#hoverOpenTimer);
    this.#hoverOpenTimer = null;
  }

  #clearHoverCloseTimer() {
    if (this.#hoverCloseTimer == null) return;
    clearTimeout(this.#hoverCloseTimer);
    this.#hoverCloseTimer = null;
  }

  /**
   * Mouse hover is the only input that waits. Focus, blur, and touch stay immediate.
   * @param {PointerEvent | FocusEvent} event
   */
  #isMouseHover(event) {
    return (
      event.type.startsWith("pointer") &&
      /** @type {PointerEvent} */ (event).pointerType === "mouse"
    );
  }

  /**
   * Activate the selected menu item.
   * Mouse hover waits before opening a closed menu, and switches immediately when one is already open.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (this.#isRestoringFocus) return;
    delete this.dataset.suppressed;
    if (document.querySelector(".header-guidance__details[open]")) return;

    // Re-entering the item or panel cancels a pending close.
    this.#clearHoverCloseTimer();

    const delayOpen =
      this.#isMouseHover(event) && this.#state.activeItem == null;
    if (delayOpen) {
      this.#clearHoverOpenTimer();
      this.#hoverOpenTimer = setTimeout(() => {
        this.#hoverOpenTimer = null;
        this.#activateImmediate(event);
      }, HOVER_OPEN_DELAY_MS);
      return;
    }

    this.#clearHoverOpenTimer();
    this.#activateImmediate(event);
  };

  /**
   * Open the selected menu item immediately.
   * @param {PointerEvent | FocusEvent} event
   */
  #activateImmediate = (event) => {
    if (document.querySelector(".header-guidance__details[open]")) return;
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;
    if (event.target.closest('[ref="submenu[]"]')) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const listItem = event.target.closest(".menu-list__list-item");
    const isDefaultSlot =
      !(listItem instanceof HTMLElement) || listItem.slot === "";

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      this.#setExpanded(previouslyActiveItem, false);
    }

    this.#state.activeItem = item;
    this.ariaExpanded = "true";
    this.#setExpanded(item, true);

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = "";

      const resetOverlayCarousel = () => {
        const overlayCarousel =
          /** @type {(HTMLElement & { select?: (input: number, event?: Event, options?: { animate?: boolean }) => void }) | null} */ (
            submenu.querySelector(".mega-menu__carousel-slideshow")
          );
        const scroller = overlayCarousel?.querySelector("slideshow-slides");

        if (!(scroller instanceof HTMLElement)) return;

        scroller.scrollTo({ left: 0, behavior: "instant" });

        if (typeof overlayCarousel?.select === "function") {
          overlayCarousel.select(0, undefined, { animate: false });
        }
      };

      const isMovingFromSameSubmenu =
        event.relatedTarget instanceof Node &&
        submenu.contains(event.relatedTarget);
      const hasRecentOverlayCarouselInteraction =
        performance.now() - this.#lastOverlayCarouselInteractionAt < 750;

      if (!isMovingFromSameSubmenu && !hasRecentOverlayCarouselInteraction) {
        resetOverlayCarousel();
      }

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty(
                "--submenu-height",
                `${submenu.offsetHeight}px`,
              );
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {
        childList: true,
        subtree: true,
      });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty(
      "--submenu-height",
      `${finalHeight}px`,
    );
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty("--submenu-opacity", "1");
  };

  /**
   * Deactivate the active item after a delay on mouse leave.
   * The close waits {@link HOVER_CLOSE_DELAY_MS} and is cancelled if the pointer
   * re-enters the item or panel. Focus blur and touch close immediately.
   * A pending open is cancelled when the pointer leaves before it elapses.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (this.dataset.devKeepFirstOverlay === "true") return;
    if (!(event.target instanceof Element)) return;

    const relatedTarget = event.relatedTarget;
    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu =
      document.activeElement instanceof Node &&
      menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      relatedTarget instanceof Node && menu?.contains(relatedTarget);
    const isMovingToOverflowMenu =
      relatedTarget instanceof Element &&
      Boolean(relatedTarget.closest('[slot="overflow"]'));

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      this.#clearHoverCloseTimer();
      return;
    }

    this.#clearHoverOpenTimer();

    if (!this.#isMouseHover(event)) {
      this.#clearHoverCloseTimer();
      const item = this.#state.activeItem;
      if (!item) return;

      if (event.type === "blur") {
        // Let the browser finish moving focus before hiding the focused submenu.
        requestAnimationFrame(() => this.#deactivate(item));
        return;
      }

      this.#deactivate(item);
      return;
    }

    const item = this.#state.activeItem;
    if (!item) return;

    this.#clearHoverCloseTimer();
    this.#hoverCloseTimer = setTimeout(() => {
      this.#hoverCloseTimer = null;
      this.#deactivate(item);
    }, HOVER_CLOSE_DELAY_MS);
  }

  /**
   * Deactivate the active item immediately.
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force] Close even while the pointer is still over the panel.
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (this.dataset.devKeepFirstOverlay === "true") return;
    if (!item || item != this.#state.activeItem) return;

    // Pointer leave waits if the panel is still hovered. Escape and the Expand control close anyway.
    if (
      !force &&
      (this.overflowListHovered || this.overflowMenu?.matches(":hover"))
    )
      return;

    this.#clearHoverOpenTimer();
    this.#clearHoverCloseTimer();
    if (force) this.dataset.suppressed = "";

    this.headerComponent?.style.setProperty("--submenu-height", "0px");
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty("--submenu-opacity", "0");
    this.dataset.overflowExpanded = "false";

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = "false";
    this.#setExpanded(item, false);

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu
      ?.querySelector("slot")
      ?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (
          link.querySelector('[ref="submenu[]"]')
        );
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty("display", "none");
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty("display");
    });
    return height;
  }

  /**
   * Keep full open header height disabled for the overlay implementation.
   * @param {number} _submenuHeight
   */
  #setFullOpenHeaderHeight(_submenuHeight) {
    if (!this.headerComponent) return;

    this.headerComponent?.style.setProperty("--full-open-header-height", "0px");
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute("loading"));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get("header-menu")) {
  customElements.define("header-menu", HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const listItem = element.closest(".menu-list__list-item") ?? element;
  if (listItem.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(
      listItem.parentElement?.querySelector('[slot="overflow"]'),
    );
  }

  const menuItem = listItem.querySelector('[ref="menuitem"]');
  return menuItem instanceof HTMLElement ? menuItem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const listItem = element?.closest(".menu-list__list-item");
  const submenu = listItem?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
